import type { ChangeEvent } from "react";
import type {
  MomentButton,
  MomentCarouselItem,
  MomentCommerceIntro,
  MomentCoupon,
  MomentListItem,
  MomentMoreButton,
  MomentMessageType,
} from "../data/momentOptions";
import { getMaxButtons } from "../data/momentOptions";
import SendInfoSection from "./composer/SendInfoSection";
import BasicTextFields from "./composer/BasicTextFields";
import WideMessageFields from "./composer/WideMessageFields";
import WideListFields from "./composer/WideListFields";
import CarouselFields from "./composer/CarouselFields";
import MoreButtonSection from "./composer/MoreButtonSection";
import ButtonSection from "./composer/ButtonSection";
import ShareSection from "./composer/ShareSection";
import CouponSection from "./composer/CouponSection";

type Props = {
  messageName: string;
  channel: string;
  channels: string[];
  messageType: MomentMessageType;
  body: string;
  messageTitle: string;
  imageName: string | null;
  buttons: MomentButton[];
  buttonsEnabled: boolean;
  titleEnabled: boolean;
  ageAuthEnabled: boolean;
  adFlag: boolean;
  shareFlag: boolean;
  couponEnabled: boolean;
  coupon: MomentCoupon;
  moreButtonEnabled: boolean;
  landingPcUrl: string;
  landingMobileUrl: string;
  moreButton: MomentMoreButton;
  listItems: MomentListItem[];
  carouselItems: MomentCarouselItem[];
  commerceIntroEnabled: boolean;
  commerceIntro: MomentCommerceIntro;
  channelLocked?: boolean;
  messageTypeLocked?: boolean;
  messageTypeNotice?: string | null;
  onMessageNameChange: (value: string) => void;
  onChannelChange: (value: string) => void;
  onMessageTypeChange: (value: MomentMessageType) => void;
  onBodyChange: (value: string) => void;
  onMessageTitleChange: (value: string) => void;
  onImageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onLibrarySelect?: (imgUrl: string) => void;
  onButtonChange: (id: string, key: keyof MomentButton, value: string) => void;
  onAddButton: () => void;
  onRemoveButton: (id: string) => void;
  onButtonsEnabledChange: (val: boolean) => void;
  onTitleEnabledChange: (val: boolean) => void;
  onAgeAuthChange: (value: boolean) => void;
  onAdFlagChange: (value: boolean) => void;
  onShareFlagChange: (value: boolean) => void;
  onCouponEnabledChange: (value: boolean) => void;
  onCouponChange: (key: keyof MomentCoupon, value: string) => void;
  onMoreButtonEnabledChange: (value: boolean) => void;
  onLandingChange: (key: "pc" | "mobile", value: string) => void;
  onMoreButtonChange: (key: "pcLandingUrl" | "mobileLandingUrl", value: string) => void;
  onListItemChange: (id: string, key: "description" | "pcLandingUrl" | "mobileLandingUrl", value: string) => void;
  onListItemImageChange: (id: string, event: ChangeEvent<HTMLInputElement>) => void;
  onListItemLibrarySelect?: (id: string, imgUrl: string) => void;
  onAddListItem: () => void;
  onRemoveListItem: (id: string) => void;
  onCarouselItemChange: (
    id: string,
    key:
      | "messageTitle" | "description" | "pcLandingUrl" | "mobileLandingUrl"
      | "buttonTitle" | "buttonPcLandingUrl" | "buttonMobileLandingUrl"
      | "secondaryButtonTitle" | "secondaryButtonPcLandingUrl" | "secondaryButtonMobileLandingUrl"
      | "secondaryButtonEnabled"
      | "priceAmount" | "discountedPriceAmount",
    value: string | boolean
  ) => void;
  onCarouselItemImageChange: (id: string, event: ChangeEvent<HTMLInputElement>) => void;
  onCarouselItemLibrarySelect?: (id: string, imgUrl: string) => void;
  onCommerceIntroImageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onCommerceIntroLibrarySelect?: (imgUrl: string) => void;
  onCommerceIntroEnabledChange: (value: boolean) => void;
  onCommerceIntroChange: (
    key: "messageTitle" | "description" | "pcLandingUrl" | "mobileLandingUrl",
    value: string
  ) => void;
  onAddCarouselItem: () => void;
  onRemoveCarouselItem: (id: string) => void;
  onCarouselItemShareFlagChange: (id: string, value: boolean) => void;
  onCarouselItemSecondaryButtonEnabledChange: (id: string, value: boolean) => void;
  onCarouselItemCouponEnabledChange: (id: string, value: boolean) => void;
  onCarouselItemCouponChange: (id: string, key: keyof MomentCoupon, value: string) => void;
};

export default function MomentMessageComposer(props: Props) {
  const {
    messageName, channel, channels, messageType, body, messageTitle,
    imageName, buttons, buttonsEnabled, titleEnabled, ageAuthEnabled, adFlag,
    shareFlag, couponEnabled, coupon, moreButtonEnabled, landingPcUrl, landingMobileUrl,
    moreButton, listItems, carouselItems, commerceIntroEnabled, commerceIntro,
    channelLocked, messageTypeLocked, messageTypeNotice,
    onMessageNameChange, onChannelChange, onMessageTypeChange,
    onBodyChange, onMessageTitleChange, onImageChange, onLibrarySelect,
    onButtonChange, onAddButton, onRemoveButton,
    onButtonsEnabledChange, onTitleEnabledChange,
    onAgeAuthChange, onAdFlagChange, onShareFlagChange,
    onCouponEnabledChange, onCouponChange, onMoreButtonEnabledChange,
    onLandingChange, onMoreButtonChange,
    onListItemChange, onListItemImageChange, onListItemLibrarySelect, onAddListItem, onRemoveListItem,
    onCarouselItemChange, onCarouselItemImageChange, onCarouselItemLibrarySelect,
    onCommerceIntroImageChange, onCommerceIntroLibrarySelect, onCommerceIntroEnabledChange, onCommerceIntroChange,
    onAddCarouselItem, onRemoveCarouselItem,
    onCarouselItemShareFlagChange, onCarouselItemSecondaryButtonEnabledChange, onCarouselItemCouponEnabledChange, onCarouselItemCouponChange,
  } = props;

  const maxButtons = getMaxButtons(messageType);
  const hasMessageLevelButtons = maxButtons > 0;
  const isCarousel = messageType === "CAROUSEL_FEED_MESSAGE" || messageType === "CAROUSEL_COMMERCE_MESSAGE";
  const supportsShareFlag =
    messageType === "BASIC_TEXT_MESSAGE" ||
    messageType === "WIDE_MESSAGE" ||
    messageType === "WIDE_LIST_MESSAGE" ||
    messageType === "CAROUSEL_COMMERCE_MESSAGE";
  const supportsMessageCoupon =
    messageType === "BASIC_TEXT_MESSAGE" ||
    messageType === "WIDE_MESSAGE" ||
    messageType === "WIDE_LIST_MESSAGE";

  return (
    <div className="tw-space-y-3">
      <SendInfoSection
        messageName={messageName}
        channel={channel}
        channels={channels}
        messageType={messageType}
        ageAuthEnabled={ageAuthEnabled}
        adFlag={adFlag}
        channelLocked={channelLocked}
        messageTypeLocked={messageTypeLocked}
        messageTypeNotice={messageTypeNotice}
        onMessageNameChange={onMessageNameChange}
        onChannelChange={onChannelChange}
        onMessageTypeChange={onMessageTypeChange}
        onAgeAuthChange={onAgeAuthChange}
        onAdFlagChange={onAdFlagChange}
      />

      {messageType === "WIDE_MESSAGE" && (
        <WideMessageFields
          body={body}
          messageTitle={messageTitle}
          imageName={imageName}
          landingPcUrl={landingPcUrl}
          landingMobileUrl={landingMobileUrl}
          titleEnabled={titleEnabled}
          onBodyChange={onBodyChange}
          onMessageTitleChange={onMessageTitleChange}
          onImageChange={onImageChange}
          onLibrarySelect={onLibrarySelect}
          onLandingChange={onLandingChange}
          onTitleEnabledChange={onTitleEnabledChange}
        />
      )}

      {messageType === "WIDE_LIST_MESSAGE" && (
        <WideListFields
          messageTitle={messageTitle}
          listItems={listItems}
          onMessageTitleChange={onMessageTitleChange}
          onListItemChange={onListItemChange}
          onListItemImageChange={onListItemImageChange}
          onListItemLibrarySelect={onListItemLibrarySelect}
          onAddListItem={onAddListItem}
          onRemoveListItem={onRemoveListItem}
        />
      )}

      {messageType === "BASIC_TEXT_MESSAGE" && (
        <BasicTextFields body={body} onBodyChange={onBodyChange} />
      )}

      {isCarousel && (
        <>
          <CarouselFields
            messageType={messageType}
            carouselItems={carouselItems}
            commerceIntroEnabled={commerceIntroEnabled}
            commerceIntro={commerceIntro}
            onCarouselItemChange={onCarouselItemChange}
            onCarouselItemImageChange={onCarouselItemImageChange}
            onCarouselItemLibrarySelect={onCarouselItemLibrarySelect}
            onCommerceIntroImageChange={onCommerceIntroImageChange}
            onCommerceIntroLibrarySelect={onCommerceIntroLibrarySelect}
            onCommerceIntroEnabledChange={onCommerceIntroEnabledChange}
            onCommerceIntroChange={onCommerceIntroChange}
            onAddCarouselItem={onAddCarouselItem}
            onRemoveCarouselItem={onRemoveCarouselItem}
            onCarouselItemShareFlagChange={onCarouselItemShareFlagChange}
            onCarouselItemSecondaryButtonEnabledChange={onCarouselItemSecondaryButtonEnabledChange}
            onCarouselItemCouponEnabledChange={onCarouselItemCouponEnabledChange}
            onCarouselItemCouponChange={onCarouselItemCouponChange}
          />
          <MoreButtonSection
            messageType={messageType}
            moreButtonEnabled={moreButtonEnabled}
            moreButton={moreButton}
            onMoreButtonEnabledChange={onMoreButtonEnabledChange}
            onMoreButtonChange={onMoreButtonChange}
          />
        </>
      )}

      {hasMessageLevelButtons && (
        <ButtonSection
          buttons={buttons}
          maxButtons={maxButtons}
          buttonsEnabled={buttonsEnabled}
          onButtonsEnabledChange={onButtonsEnabledChange}
          onButtonChange={onButtonChange}
          onAddButton={onAddButton}
          onRemoveButton={onRemoveButton}
        />
      )}

      {supportsShareFlag && (
        <ShareSection
          messageType={messageType}
          shareFlag={shareFlag}
          onShareFlagChange={onShareFlagChange}
        />
      )}

      {supportsMessageCoupon && (
        <CouponSection
          messageType={messageType}
          couponEnabled={couponEnabled}
          coupon={coupon}
          onCouponEnabledChange={onCouponEnabledChange}
          onCouponChange={onCouponChange}
        />
      )}
    </div>
  );
}
